import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Form, notification, Rate, Select } from 'antd';
import {
  useSubTopicsQuery,
  useUpdateConsultationMutation,
  useViolatorsQuery,
} from 'app/services/edpApiV1/consultations';
import { useAppDispatch, useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import { getErrorData } from 'shared/helpers/utils';
import { TReason } from 'shared/types/types';
import { TSingleConsultationPreparedData } from 'shared/types/consultations.types';
import { viewerModel } from 'entities/viewer';
import style from './index.module.scss';

const ConsultationFormAdditionally = ({
  dataConsultations,
  consultationRefetch,
}: {
  dataConsultations: TSingleConsultationPreparedData[];
  consultationRefetch: () => void;
}) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { id: myId } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const {
    isClaim,
    subtopicId,
    isJustified,
    violationId,
    violationReasons,
    violationReasonId,
    asInitFormvals,
    feedback,
    isSaveBtnVisible: isVisible,
  } = useAppSelector(uiModel.Consultations.selectors.selectConsultations);
  // disable для зависимых инпутов
  const hasValsForIsJustified = !!subtopicId && isClaim;
  const hasValsForIsviolationId = hasValsForIsJustified && isJustified !== null;
  const hasValsForViolationReasonId = hasValsForIsJustified && !!violationId;
  // все ли поля претензити заполнены
  const isClaimReadyToSubmit = hasValsForViolationReasonId && !!violationReasonId;
  // все ли поля консультации заполнены
  const isConsultReadyToSubmit = !!subtopicId && isClaim === false;
  // disable для кнопки Сохранить
  const readyToSubmit = isClaimReadyToSubmit || isConsultReadyToSubmit;
  // резрешение на изменение полей для собственных заявок
  const isAllowToChange = dataConsultations[0]?.consultant.id !== myId;

  const topicId = dataConsultations[0]?.topicid;

  const { data: dataSubTopics } = useSubTopicsQuery(topicId, { skip: !topicId });

  const [updateConsultation, resultUpdate] = useUpdateConsultationMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const { data: allViolators } = useViolatorsQuery({});

  const {
    isSuccess: successUpdateConsultation,
    isError: errorUpdateConsultation,
    // reset: resetUpdateConsultation,
    error: errorUpdate,
  } = resultUpdate;

  // при изменении полей формы очищаю зависимые поля
  const onChangeController = (changedVal: Record<string, boolean | number>) => {
    const changedField = Object.keys(changedVal)[0];

    if (subtopicId && changedField === 'subtopicId') {
      if (subtopicId === changedVal[changedField]) return;
      dispatch(uiModel.Consultations.actions.resetFieldsByName(['violationId', 'isJustified', 'violationReasonId']));
      form.resetFields(['violationId', 'isJustified', 'violationReasonId']);
    }

    if (isJustified !== null && changedField === 'isJustified') {
      if (isJustified === changedVal[changedField]) return;
      dispatch(uiModel.Consultations.actions.resetFieldsByName(['violationId', 'violationReasonId']));
      form.resetFields(['violationId', 'violationReasonId']);
    }

    if (violationId && changedField === 'violationId') {
      if (violationId === changedVal[changedField]) return;
      dispatch(uiModel.Consultations.actions.resetFieldsByName(['violationReasonId']));
      form.resetFields(['violationReasonId']);
    }

    if (changedField === 'isClaim' && (subtopicId || isJustified !== null || violationId || violationReasons)) {
      dispatch(
        uiModel.Consultations.actions.resetFieldsByName([
          'subtopicId',
          'isJustified',
          'violationId',
          'violationReasons',
        ]),
      );
      form.resetFields(['subtopicId', 'isJustified', 'violationId', 'violationReasons']);
      if (isVisible) {
        dispatch(uiModel.Consultations.actions.isSaveBtnVisible(false));
      }
    }
    // для отображения кнопки сохранить при смене значения в инпуте Категория обращения
    //  при выбраyном Виде Запроса Консультация
    if (changedField === 'subtopicId' && isClaim === false) {
      const initSubtopicId = dataConsultations[0]?.subtopic.id;
      if (initSubtopicId !== subtopicId) {
        dispatch(uiModel.Consultations.actions.isSaveBtnVisible(true));
      }
    }
  };
  // ассинхронность нужна для правильного порядка запросов,
  // в противном случае, данные о консультации ещё не обновились и consultationRefetch
  // вёрнёт старые значения
  const onFinish = async (values: {
    isClaim: boolean;
    isJustified: boolean;
    subtopicId: number;
    violationReasonId: number;
  }) => {
    const { isClaim, isJustified, subtopicId, violationReasonId } = values;
    if (id) {
      const bodyRequest = {
        chatId: id,
        isClaim,
        isJustified,
        subtopicId,
        violationReasonId,
      };
      await updateConsultation(bodyRequest);
      dispatch(uiModel.Consultations.actions.isSaveBtnVisible(false));
      await consultationRefetch();
    }
  };

  // селект подразделение нарушитель
  const onSelectViolatorsId = (id: number) => {
    const reasonData = allViolators?.find(elem => elem?.id === id)?.violationReasons as TReason[];
    dispatch(uiModel.Consultations.actions.violationId(id));
    if (reasonData) {
      dispatch(uiModel.Consultations.actions.violationReasons(reasonData));
    }
  };

  // селект вид запроса
  const onSelectIsClaim = (value: boolean) => {
    dispatch(uiModel.Consultations.actions.isClaim(value));
    if (!value) {
      dispatch(
        uiModel.Consultations.actions.resetFieldsByName([
          'subtopicId',
          'violationId',
          'isJustified',
          'violationReasonId',
        ]),
      );
      form.resetFields(['subtopicId', 'violationId', 'isJustified', 'violationReasonId']);
    }
  };

  // селект обоснованности
  const onSelectIsJustified = (value: boolean) => {
    dispatch(uiModel.Consultations.actions.isJustified(value));
  };

  // селект категории обращения
  const onSelectSubTopicId = (value: number) => {
    dispatch(uiModel.Consultations.actions.subtopicId(value));
  };

  // селект причины нарушения
  const onSelectViolationReasonId = (value: number) => {
    dispatch(uiModel.Consultations.actions.violationReasonId(value));
  };

  // логика проверки сравнения актуальных данных формвы  с даннымы с бэка,
  // это надо для правильного отображения кнопки СОХРАНИТЬ
  const checkFormOnEqualToInitial = () => {
    const savedData = dataConsultations[0];
    const formData = form.getFieldsValue();
    if (savedData && formData) {
      dispatch(
        uiModel.Consultations.actions.checkInitVals({ saved: dataConsultations[0], form: form.getFieldsValue() }),
      );
    }
  };

  // логика инициализации списка селекта Причина нарушения
  useEffect(() => {
    if (dataConsultations[0]?.violator) {
      const violator = allViolators?.find(elem => elem?.id === dataConsultations[0]?.violator?.id)
        ?.violationReasons as TReason[];
      if (violator) {
        dispatch(uiModel.Consultations.actions.violationReasons(violator));
      }
    }
  }, [dataConsultations, allViolators]);

  // логика инициализации формы
  useEffect(() => {
    if (dataConsultations && dataConsultations?.length > 0) {
      if (dataConsultations[0]?.isClaim !== null) {
        form.setFields([
          {
            name: 'isClaim',
            value: dataConsultations[0]?.isClaim,
          },
        ]);
        dispatch(uiModel.Consultations.actions.isClaim(dataConsultations[0]?.isClaim));
      }
      if (dataConsultations[0]?.isJustified !== null) {
        form.setFields([
          {
            name: 'isJustified',
            value: dataConsultations[0]?.isJustified,
          },
        ]);
        dispatch(uiModel.Consultations.actions.isJustified(dataConsultations[0]?.isJustified));
      }
      if (dataConsultations[0]?.subtopic?.id) {
        form.setFields([
          {
            name: 'subtopicId',
            value: dataConsultations[0]?.subtopic?.id,
          },
        ]);
        dispatch(uiModel.Consultations.actions.subtopicId(dataConsultations[0]?.subtopic?.id));
      }
      if (dataConsultations[0]?.violator?.id) {
        form.setFields([
          {
            name: 'violationId',
            value: dataConsultations[0]?.violator?.id,
          },
        ]);
        dispatch(uiModel.Consultations.actions.violationId(dataConsultations[0]?.violator?.id));
      }
      if (dataConsultations[0]?.violationReason?.id) {
        form.setFields([
          {
            name: 'violationReasonId',
            value: dataConsultations[0]?.violationReason?.id,
          },
        ]);
        dispatch(uiModel.Consultations.actions.violationReasonId(dataConsultations[0]?.violationReason?.id));
      }
      if (dataConsultations[0]?.feedback) {
        dispatch(uiModel.Consultations.actions.feedback(dataConsultations[0]?.feedback));
      }
      //если есть информация по консультации, то проверяю не равны ли значения формы данным
      checkFormOnEqualToInitial();
    }
  }, [dataConsultations, form]);

  useEffect(() => {
    if (successUpdateConsultation) {
      notification.success({
        message: 'Информация обновлена',
      });
    }
  }, [successUpdateConsultation]);

  useEffect(() => {
    if (errorUpdateConsultation) {
      notification.error({
        message: errorUpdate?.message,
      });
    }
  }, [errorUpdateConsultation, errorUpdate]);

  useEffect(() => {
    return () => {
      dispatch(uiModel.Consultations.actions.clearValues());
    };
  }, []);

  // проверка на выполнение положительного сценария для показа кнопки Сохранить
  useEffect(() => {
    if (readyToSubmit && !asInitFormvals) {
      dispatch(uiModel.Consultations.actions.isSaveBtnVisible(true));
    }
  }, [readyToSubmit, asInitFormvals]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onValuesChange={(data: Record<string, boolean | number>) => {
        onChangeController(data);
        // запускаю проверку на равенство значений формы и данных с бэка на каждый чих
        checkFormOnEqualToInitial();
      }}
      name="userInfo"
    >
      <div className={style.groupItem}>
        <Form.Item
          name="isClaim"
          label="Вид запроса"
          labelAlign="left"
          className={style.inputWrap}
          rules={[{ required: true, message: 'Пожалуйста, выберите вариант' }]}
        >
          <Select
            placeholder="Выберите вид запроса"
            onSelect={(value: boolean) => onSelectIsClaim(value)}
            disabled={isAllowToChange}
          >
            <Select.Option value={true}>Претензия</Select.Option>
            <Select.Option value={false}>Консультация</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="isJustified" label="Обоснованность претензии" labelAlign="left" className={style.inputWrap}>
          <Select
            placeholder="Выберите вариант"
            disabled={!hasValsForIsJustified || isAllowToChange}
            onSelect={(value: boolean) => onSelectIsJustified(value)}
          >
            <Select.Option value={true}>Обоснована</Select.Option>
            <Select.Option value={false}>Не обоснована</Select.Option>
          </Select>
        </Form.Item>
      </div>
      <div className={style.groupItem}>
        <Form.Item
          name="subtopicId"
          label="Категория обращения"
          labelAlign="left"
          className={style.inputWrap}
          rules={[{ required: true, message: 'Пожалуйста, выберите вариант' }]}
        >
          <Select
            placeholder="Введите вариант"
            onSelect={(value: number) => onSelectSubTopicId(value)}
            disabled={isAllowToChange}
          >
            {dataSubTopics?.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="violationId" label="Подразделение-нарушитель" labelAlign="left" className={style.inputWrap}>
          <Select
            placeholder="Выберите вариант"
            onSelect={(id: number) => onSelectViolatorsId(id)}
            disabled={!hasValsForIsviolationId || isAllowToChange}
          >
            {allViolators?.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className={style.groupItem}>
        <Form.Item name="violationReasonId" label="Причина нарушения" labelAlign="left" className={style.inputWrap}>
          <Select
            placeholder="Выберите вариант"
            disabled={!hasValsForViolationReasonId || isAllowToChange}
            onSelect={(value: number) => onSelectViolationReasonId(value)}
          >
            {violationReasons?.map(({ id, name }) => (
              <Select.Option key={id} value={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {feedback && (
          <div className={style.rate}>
            <div>Оценка консультации:</div>
            <Rate disabled value={feedback.stars} />
          </div>
        )}
      </div>
      {feedback?.comment && (
        <div className={style.comment}>
          <div>Комментарий:</div>
          <span>{feedback.comment}</span>
        </div>
      )}
      {readyToSubmit && !asInitFormvals && isVisible && (
        <Form.Item labelAlign="left" className={style.btn}>
          <Button htmlType="submit" type="primary">
            Сохранить
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default ConsultationFormAdditionally;
